export class PhonicsChunker {
    private static readonly DIGRAPHS = ['sh', 'ch', 'th', 'wh', 'ph', 'ck', 'ng'];
    private static readonly VOWELS = ['a', 'e', 'i', 'o', 'u'];
    private static readonly VOWEL_TEAMS = ['ai', 'ay', 'ea', 'ee', 'oa', 'ow', 'oo', 'ou', 'oi', 'oy'];
    private static readonly R_CONTROLLED = ['ar', 'er', 'ir', 'or', 'ur'];
  
    static chunk(word: string, pattern: string): string[] {
      const lower = word.toLowerCase();
      
      switch(pattern) {
        // NEW full pattern names
        case 'cvc_short_vowels': return this.chunkCVC(lower);
        case 'magic_e_cvce': return this.chunkMagicE(lower);
        case 'digraphs': return this.chunkDigraph(lower);
        case 'consonant_blends': return this.chunkBlend(lower);
        case 'vowel_teams': return this.chunkVowelTeam(lower);
        case 'r_controlled': return this.chunkRControlled(lower);
        
        // OLD short names for backwards compatibility
        case 'cvc': return this.chunkCVC(lower);
        case 'magic_e': return this.chunkMagicE(lower);
        case 'digraph': return this.chunkDigraph(lower);
        case 'blend': return this.chunkBlend(lower);
        case 'vowel_team': return this.chunkVowelTeam(lower);
        
        default: return [lower]; // Fallback
      }
    }
  
      private static chunkCVC(word: string): string[] {
        // Handle digraph endings (ck, sh, th, ch, ng, tch)
        const digraphEndings = ['tch', 'ck', 'sh', 'th', 'ch', 'ng'];
        
        for (const ending of digraphEndings) {
          if (word.endsWith(ending)) {
            const rest = word.slice(0, -ending.length);
            // Standard CVC with digraph: c-a-t vs h-a-ck
            if (rest.length === 2) {
              return [rest[0], rest[1], ending];
            }
            // Blend + vowel + digraph: tr-a-ck
            if (rest.length === 3) {
              return [rest.slice(0, 2), rest[2], ending];
            }
            // Longer words: keep ending together
            return [rest.slice(0, -1), rest.slice(-1), ending];
          }
        }
        
        // No digraph - standard CVC split
        if (word.length === 3) return word.split('');
        if (word.length === 4) return [word[0], word[1], word[2], word[3]];
        return [word.slice(0, -2), word.slice(-2)];
    }
  
    private static chunkMagicE(word: string): string[] {
        if (word.length < 4) return [word];
        
        const beforeE = word.slice(0, -1);
        const lastE = word.slice(-1);
        
        // Check for THREE-letter blends first (str, spl, thr, etc)
        const threeBlends = ['str', 'spl', 'spr', 'scr', 'thr', 'shr'];
        for (const blend of threeBlends) {
          if (beforeE.startsWith(blend)) {
            const vowel = beforeE[3];
            const finalConsonant = beforeE.slice(4);
            return [blend, vowel, finalConsonant + lastE];
          }
        }
        
        // Check for TWO-letter blends
        const twoBlends = ['bl', 'br', 'cl', 'cr', 'dr', 'fl', 'fr', 'gl', 'gr', 'pl', 'pr', 'sc', 'sk', 'sl', 'sm', 'sn', 'sp', 'st', 'sw', 'tr', 'tw'];
        const firstTwo = beforeE.slice(0, 2);
        if (twoBlends.includes(firstTwo)) {
          const vowel = beforeE[2];
          const finalConsonant = beforeE.slice(3);
          return [firstTwo, vowel, finalConsonant + lastE];
        }
        
        // Check for digraphs at start
        const digraphs = ['sh', 'ch', 'th', 'wh', 'ph'];
        if (digraphs.includes(firstTwo)) {
          const vowel = beforeE[2];
          const finalConsonant = beforeE.slice(3);
          return [firstTwo, vowel, finalConsonant + lastE];
        }
        
        // Standard: single consonant onset
        return [beforeE[0], beforeE[1], beforeE.slice(2) + lastE];
    }
  
    private static chunkDigraph(word: string): string[] {
        const digraphs = ['sh', 'ch', 'th', 'wh', 'ph', 'ck', 'ng', 'tch'];
        
        for (const dg of digraphs) {
          if (word.includes(dg)) {
            const idx = word.indexOf(dg);
            
            // Digraph at START
            if (idx === 0) {
              const rest = word.slice(dg.length);
              // sh-o-p (3 letters after digraph)
              if (rest.length <= 3) {
                return [dg, rest[0], rest.slice(1)].filter(p => p.length > 0);
              }
              // sh-a-dow (longer words)
              return [dg, rest.slice(0, 2), rest.slice(2)].filter(p => p.length > 0);
            }
            
            // Digraph at END
            if (idx === word.length - dg.length) {
              const before = word.slice(0, idx);
              // fi-sh (short word)
              if (before.length <= 2) {
                return [before, dg];
              }
              // dish-i-ng would be caught here - split before digraph
              return [before.slice(0, -1), before.slice(-1), dg].filter(p => p.length > 0);
            }
            
            // Digraph in MIDDLE (rare)
            const before = word.slice(0, idx);
            const after = word.slice(idx + dg.length);
            return [before, dg, after].filter(p => p.length > 0);
          }
        }
        return [word];
    }
  
    private static chunkBlend(word: string): string[] {
        const threeBlends = ['str', 'spl', 'spr', 'scr', 'thr', 'shr'];
        const twoBlends = ['bl', 'br', 'cl', 'cr', 'dr', 'fl', 'fr', 'gl', 'gr', 'pl', 'pr', 'sc', 'sk', 'sl', 'sm', 'sn', 'sp', 'st', 'sw', 'tr', 'tw'];
        
        // Try three-letter blends first
        for (const blend of threeBlends) {
          if (word.startsWith(blend)) {
            const rest = word.slice(3);
            return this.chunkAfterBlend(blend, rest);
          }
        }
        
        // Try two-letter blends
        for (const blend of twoBlends) {
          if (word.startsWith(blend)) {
            const rest = word.slice(2);
            return this.chunkAfterBlend(blend, rest);
          }
        }
        
        // No blend found
        console.warn(`⚠️ chunkBlend() called on non-blend word: ${word}`);
        return [word];
    }
    
    // Helper method for blend chunking
    private static chunkAfterBlend(blend: string, rest: string): string[] {
        // Check for digraph endings
        const digraphs = ['tch', 'ck', 'sh', 'th', 'ch', 'ng'];
        for (const dg of digraphs) {
          if (rest.endsWith(dg)) {
            const middle = rest.slice(0, -dg.length);
            if (middle.length === 1) {
              return [blend, middle, dg]; // tr-a-ck
            }
            if (middle.length === 2) {
              return [blend, middle[0], middle[1] + dg]; // tr-ea-ch
            }
          }
        }
        
        // No digraph - standard split
        if (rest.length === 2) {
          return [blend, rest[0], rest[1]]; // tr-a-p
        }
        if (rest.length === 3) {
          return [blend, rest[0], rest.slice(1)]; // tr-a-sh
        }
        if (rest.length >= 4) {
          return [blend, rest.slice(0, 2), rest.slice(2)]; // tr-ain-ed
        }
        
        return [blend, rest];
    }
  
    private static chunkVowelTeam(word: string): string[] {
        const vowelTeams = ['ai', 'ay', 'ea', 'ee', 'oa', 'ow', 'oo', 'ou', 'oi', 'oy', 'au', 'aw', 'ew', 'ue'];
        
        for (const vt of vowelTeams) {
          if (word.includes(vt)) {
            const idx = word.indexOf(vt);
            const before = word.slice(0, idx);
            const after = word.slice(idx + 2);
            
            // Check if 'before' is a blend - keep it together
            const blends = ['str', 'spl', 'spr', 'scr', 'thr', 'shr', 'bl', 'br', 'cl', 'cr', 'dr', 'fl', 'fr', 'gl', 'gr', 'pl', 'pr', 'sc', 'sk', 'sl', 'sm', 'sn', 'sp', 'st', 'sw', 'tr', 'tw'];
            if (blends.includes(before)) {
              return [before, vt, after].filter(p => p.length > 0);
            }
            
            // Standard split
            return [before, vt, after].filter(p => p.length > 0);
          }
        }
        return [word];
    }
  
    private static chunkRControlled(word: string): string[] {
        const rControlled = ['ar', 'er', 'ir', 'or', 'ur'];
        
        for (const rc of rControlled) {
          if (word.includes(rc)) {
            const idx = word.indexOf(rc);
            const before = word.slice(0, idx);
            const after = word.slice(idx + 2);
            
            // Check if 'before' is a blend - keep it together
            const blends = ['str', 'spl', 'spr', 'scr', 'thr', 'shr', 'bl', 'br', 'cl', 'cr', 'dr', 'fl', 'fr', 'gl', 'gr', 'pl', 'pr', 'sc', 'sk', 'sl', 'sm', 'sn', 'sp', 'st', 'sw', 'tr', 'tw'];
            if (blends.includes(before)) {
              return [before, rc, after].filter(p => p.length > 0);
            }
            
            // Standard split
            return [before, rc, after].filter(p => p.length > 0);
          }
        }
        return [word];
    }
  }